import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { ArrowLeft, Download, Fuel, Gauge, TrendingUp, Trash2, Plus, Car, Wrench, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import ReceiptViewer, { ReceiptEmpty } from "@/components/ReceiptViewer";
import DateRangePicker, { useDateRange } from "@/components/DateRangePicker";
import VehicleDocuments from "@/components/VehicleDocuments";
import OwnershipCostsCard from "@/components/OwnershipCostsCard";
import AskRayFuel from "@/components/AskRayFuel";
import { useEntitlements } from "@/context/EntitlementsContext";

const INR = (n) => `₹${(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [refuels, setRefuels] = useState([]);
  const [trend, setTrend] = useState([]);
  const [serviceSummary, setServiceSummary] = useState(null);
  const [serviceBills, setServiceBills] = useState([]);
  const [timeline, setTimeline] = useState(null);
  const [routine, setRoutine] = useState(null);
  const [loading, setLoading] = useState(true);
  const range = useDateRange("all");

  const load = async () => {
    setLoading(true);
    try {
      const [vs, rs, ts, ss, sb, tl, ra] = await Promise.all([
        api.get("/vehicles"),
        api.get("/refuels", { params: { vehicle_id: id, limit: 1000 } }),
        api.get(`/reports/economy/${id}`),
        api.get(`/reports/vehicle-service-summary/${id}`),
        api.get("/service-bills", { params: { vehicle_id: id } }),
        api.get(`/reports/vehicle-timeline/${id}`, { params: { sort: "odometer", ...range.params } }),
        api.get(`/reports/routine-analysis/${id}`),
      ]);
      const v = vs.data.find((x) => x.id === id);
      if (!v) {
        toast.error("Vehicle not found");
        navigate("/");
        return;
      }
      setVehicle(v);
      setRefuels(rs.data);
      setTrend(ts.data.trend || []);
      setServiceSummary(ss.data);
      setServiceBills(sb.data);
      setTimeline(tl.data);
      setRoutine(ra.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, range.preset, range.from, range.to]);

  const stats = useMemo(() => {
    if (!refuels.length) return { total_amount: 0, total_litres: 0, total_km: 0, avg_mileage: null, cost_per_km: null };
    const total_amount = refuels.reduce((s, r) => s + (r.amount || 0), 0);
    const total_litres = refuels.reduce((s, r) => s + (r.litres || 0), 0);
    // refuels API returns in descending order; sort ascending by odometer to derive range safely
    const sorted = [...refuels].sort((a, b) => (a.refuelled_at || "").localeCompare(b.refuelled_at || ""));
    let total_km = 0;
    let litres_used = 0;
    let amount_used = 0;
    if (sorted.length >= 2) {
      total_km = Math.max(0, sorted[sorted.length - 1].odometer - sorted[0].odometer);
      for (let i = 1; i < sorted.length; i++) {
        litres_used += sorted[i].litres || 0;
        amount_used += sorted[i].amount || 0;
      }
    }
    return {
      total_amount,
      total_litres,
      total_km,
      avg_mileage: litres_used > 0 ? +(total_km / litres_used).toFixed(2) : null,
      cost_per_km: total_km > 0 ? +(amount_used / total_km).toFixed(2) : null,
    };
  }, [refuels]);

  const exportCsv = () => {
    if (!vehicle) return;
    const header = [
      "date_time",
      "odometer_km",
      "litres",
      "rate_inr_per_l",
      "amount_inr",
      "km_since_last",
      "mileage_km_per_l",
      "cost_per_km_inr",
      "station",
      "fuel_type",
      "notes",
    ];
    const sorted = [...refuels].sort((a, b) => (a.refuelled_at || "").localeCompare(b.refuelled_at || ""));
    const rows = sorted.map((r) => [
      (r.refuelled_at || "").replace("T", " ").slice(0, 19),
      r.odometer,
      r.litres,
      r.rate,
      r.amount,
      r.km_since_last ?? "",
      r.mileage ?? "",
      r.cost_per_km ?? "",
      r.station ?? "",
      r.fuel_type ?? "",
      r.notes ?? "",
    ]);
    const fname = `fueltrack_${vehicle.number}_${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCsv(fname, [header, ...rows]);
    toast.success("CSV downloaded");
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this refuel entry?")) return;
    try {
      await api.delete(`/refuels/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    }
  };

  if (loading) return <div className="text-[#737373]">Loading vehicle…</div>;
  if (!vehicle) return null;

  const chartData = trend.filter((t) => t.mileage != null || t.cost_per_km != null);

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3 text-[#737373]">
        <Link to="/refuels" className="inline-flex items-center gap-1 hover:text-white transition-colors duration-200 text-sm" data-testid="back-link">
          <ArrowLeft size={14} /> Back
        </Link>
      </div>

      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="ft-overline mb-2 flex items-center gap-2">
            <Car size={14} className="text-[#FF5A1F]" /> VEHICLE DEEP-DIVE
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none" data-testid="vehicle-detail-number">
            {vehicle.number}
          </h1>
          <p className="mt-3 text-[#A3A3A3]">
            {vehicle.name} · <span className="text-[#FF5A1F] font-mono">{vehicle.fuel_type}</span> ·
            <span className="font-mono ml-2">Initial odo {vehicle.initial_odometer?.toLocaleString?.() ?? 0} km</span>
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={exportCsv}
            disabled={refuels.length === 0}
            className="border border-[#262626] px-4 py-3 text-white hover:bg-[#1A1A1A] hover:border-[#FF5A1F]/50 transition-colors duration-200 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="export-csv-button"
          >
            <Download size={16} /> Export CSV
          </button>
          <Link
            to="/new"
            className="bg-[#FF5A1F] text-black px-4 py-3 font-bold inline-flex items-center gap-2 hover:bg-[#E04F1A] transition-colors duration-200"
            data-testid="new-refuel-from-detail"
          >
            <Plus size={16} /> Log Refuel
          </Link>
        </div>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 sm:gap-6">
        <Stat label="TOTAL SPENT" value={INR(stats.total_amount)} icon={<Fuel size={14} />} testid="stat-total-spent" />
        <Stat label="LITRES" value={`${stats.total_litres.toFixed(1)} L`} icon={<Gauge size={14} />} testid="stat-total-litres" />
        <Stat label="KM DRIVEN" value={`${Math.round(stats.total_km).toLocaleString()} km`} icon={<TrendingUp size={14} />} testid="stat-total-km" />
        <Stat label="AVG MILEAGE" value={stats.avg_mileage ? `${stats.avg_mileage} km/l` : "—"} icon={<Gauge size={14} />} testid="stat-avg-mileage" accent />
        <Stat label="COST / KM" value={stats.cost_per_km ? `₹${stats.cost_per_km}` : "—"} icon={<Fuel size={14} />} testid="stat-cost-per-km" />
        <Stat label="SERVICE & REPAIR" value={INR(serviceSummary?.total_service_amount)} icon={<Wrench size={14} />} testid="stat-service-amount" />
        <Stat label="TCO / KM" value={serviceSummary?.tco_per_km ? `₹${serviceSummary.tco_per_km}` : "—"} icon={<TrendingUp size={14} />} testid="stat-tco-per-km" accent />
      </div>

      {/* RC + Insurance documents (AI-scanned) */}
      <VehicleDocuments vehicleId={id} />

      {/* Ownership Costs (Operating vs full TCO) */}
      <OwnershipCostsCard vehicleId={id} />

      {/* Grounded AI chat scoped to THIS vehicle */}
      <VehicleAskWrapper vehicleId={id} />

      {/* Routine analysis */}
      {routine && routine.count > 0 && (        <div className="ft-card p-6" data-testid="routine-analysis-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="ft-overline flex items-center gap-2"><Clock size={12} className="text-[#FF5A1F]" /> ROUTINE SERVICE CADENCE</p>
              <h3 className="font-display text-2xl font-bold tracking-tight mt-1">
                {routine.count} routine service{routine.count === 1 ? "" : "s"} on record
              </h3>
            </div>
            <div className="text-right">
              <p className="ft-overline">AVG KM BETWEEN</p>
              <p className="font-mono text-3xl font-medium text-[#FF5A1F]">
                {routine.avg_km_between ? `${Math.round(routine.avg_km_between).toLocaleString()} km` : "—"}
              </p>
              <p className="text-xs text-[#737373]">interval: every {vehicle.service_interval_km || 10000} km</p>
            </div>
          </div>
        </div>
      )}

      {/* Unified timeline (fuel + service) */}
      <div className="ft-card p-6 space-y-4" data-testid="unified-timeline-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="ft-overline">UNIFIED TIMELINE</p>
            <h3 className="font-display text-2xl font-bold tracking-tight mt-1">Every fuel, charging, service &amp; document event</h3>
          </div>
          {timeline?.totals && (
            <div className="text-right text-sm">
              <p className="ft-overline">RANGE TOTALS</p>
              <p className="font-mono">
                Fuel <span className="text-white">{INR(timeline.totals.fuel)}</span>
                {" · "} Charging <span className="text-white">{INR(timeline.totals.charging || 0)}</span>
                {" · "} Service <span className="text-white">{INR(timeline.totals.service)}</span>
              </p>
              <p className="font-mono text-[#FF5A1F] text-lg">Total {INR(timeline.totals.total)}</p>
            </div>
          )}
        </div>
        <DateRangePicker
          preset={range.preset} setPreset={range.setPreset}
          from={range.from} setFrom={range.setFrom}
          to={range.to} setTo={range.setTo}
          testidPrefix="vd-"
        />
        {timeline && timeline.events.length > 0 ? (
          <div className="overflow-auto">
            <table className="w-full" data-testid="unified-timeline-table">
              <thead>
                <tr>
                  {["Odometer", "Date", "Kind", "Title", "Detail", "Amount"].map((h) => (
                    <th key={h} className="text-left py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#737373] border-b border-[#262626]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="font-mono text-sm">
                {timeline.events.map((e, i) => (
                  <tr key={`${e.kind}-${e.id || i}`} className="border-b border-[#262626] hover:bg-[#1A1A1A] transition-colors">
                    <td className="py-3 px-3 text-right text-white">{e.odometer != null ? `${Number(e.odometer).toLocaleString()} km` : "—"}</td>
                    <td className="py-3 px-3 text-[#A3A3A3]">{(e.date || "").slice(0, 10)}</td>
                    <td className="py-3 px-3">
                      <span className={`px-2 py-0.5 text-xs font-bold uppercase tracking-wider border ${
                        e.kind === "fuel" ? "border-[#FF5A1F]/40 bg-[#FF5A1F]/10 text-[#FF5A1F]"
                        : e.kind === "charging" ? "border-[#22D3EE]/40 bg-[#22D3EE]/10 text-[#22D3EE]"
                        : e.kind === "routine" ? "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]"
                        : e.kind === "repair" ? "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]"
                        : e.kind === "document" ? "border-[#A855F7]/40 bg-[#A855F7]/10 text-[#A855F7]"
                        : "border-[#3B82F6]/40 bg-[#3B82F6]/10 text-[#3B82F6]"
                      }`}>{e.kind}</span>
                    </td>
                    <td className="py-3 px-3 text-white">{e.title || "—"}</td>
                    <td className="py-3 px-3 text-[#A3A3A3] max-w-xs truncate">
                      {typeof e.detail === "string" ? e.detail : (e.detail?.description || (e.items_count ? `${e.items_count} items` : "—"))}
                    </td>
                    <td className="py-3 px-3 text-right text-white font-bold">{e.amount ? INR(e.amount) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-[#A3A3A3] text-center py-6">No events in this range.</p>
        )}
      </div>

      {/* Trend chart */}
      <div className="ft-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="ft-overline">ECONOMY TREND</p>
            <h3 className="font-display text-2xl font-bold tracking-tight mt-1">km/l & ₹/km over time</h3>
          </div>
        </div>
        <div className="h-72">
          {chartData.length >= 1 ? (
            <ResponsiveContainer>
              <LineChart data={trend} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" stroke="#737373" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis yAxisId="left" stroke="#737373" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis yAxisId="right" orientation="right" stroke="#737373" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #262626", borderRadius: 0 }} />
                <Legend wrapperStyle={{ color: "#A3A3A3", fontSize: 12 }} />
                <Line yAxisId="left" type="monotone" name="km/l" dataKey="mileage" stroke="#FF5A1F" strokeWidth={2} dot={{ r: 3, fill: "#FF5A1F" }} />
                <Line yAxisId="right" type="monotone" name="₹/km" dataKey="cost_per_km" stroke="#3B82F6" strokeWidth={2} dot={{ r: 2, fill: "#3B82F6" }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-[#737373] text-sm">
              Log at least 2 refuels to compute the trend.
            </div>
          )}
        </div>
      </div>

      {/* Timeline / refuels table */}
      <div className="ft-card p-0 overflow-auto">
        <div className="p-6 pb-3 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="ft-overline">FULL TIMELINE</p>
            <h3 className="font-display text-2xl font-bold tracking-tight mt-1">{refuels.length} refuel{refuels.length === 1 ? "" : "s"} on record</h3>
          </div>
        </div>
        {refuels.length === 0 ? (
          <div className="p-12 text-center text-[#A3A3A3]">
            No refuels yet for this vehicle.{" "}
            <Link to="/new" className="text-[#FF5A1F] hover:underline">Log the first one</Link>.
          </div>
        ) : (
          <table className="w-full" data-testid="vehicle-timeline-table">
            <thead>
              <tr>
                {["Date", "Odometer", "Litres", "Rate", "Amount", "Km Since", "Mileage", "₹/km", "Station", "Receipt", ""].map((h) => (
                  <th key={h} className="text-left py-4 px-3 text-xs uppercase tracking-wider font-bold text-[#737373] border-b border-[#262626]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="font-mono text-sm">
              {refuels.map((r) => (
                <tr key={r.id} className="border-b border-[#262626] hover:bg-[#1A1A1A] transition-colors duration-150">
                  <td className="py-3 px-3 text-[#A3A3A3]">{(r.refuelled_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="py-3 px-3 text-right">{r.odometer?.toLocaleString?.()}</td>
                  <td className="py-3 px-3 text-right">{r.litres?.toFixed(2)}</td>
                  <td className="py-3 px-3 text-right">₹{r.rate?.toFixed(2)}</td>
                  <td className="py-3 px-3 text-right text-white font-bold">₹{r.amount?.toFixed(0)}</td>
                  <td className="py-3 px-3 text-right">{r.km_since_last ?? <span className="text-[#737373]">—</span>}</td>
                  <td className="py-3 px-3 text-right text-[#FF5A1F]">{r.mileage ? `${r.mileage} km/l` : <span className="text-[#737373]">—</span>}</td>
                  <td className="py-3 px-3 text-right">{r.cost_per_km ? `₹${r.cost_per_km}` : <span className="text-[#737373]">—</span>}</td>
                  <td className="py-3 px-3 text-[#A3A3A3]">{r.station || "—"}</td>
                  <td className="py-3 px-3">{r.receipt_path ? <ReceiptViewer path={r.receipt_path} /> : <ReceiptEmpty />}</td>
                  <td className="py-3 px-3 text-right">
                    <button
                      onClick={() => remove(r)}
                      className="text-[#EF4444] hover:text-white hover:bg-[#EF4444] p-2 transition-colors duration-200"
                      data-testid={`vd-refuel-delete-${r.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Service history */}
      <div className="ft-card p-0 overflow-auto" data-testid="service-history-section">
        <div className="p-6 pb-3 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="ft-overline flex items-center gap-2"><Wrench size={12} className="text-[#FF5A1F]" /> SERVICE & REPAIR</p>
            <h3 className="font-display text-2xl font-bold tracking-tight mt-1">
              {serviceBills.length} bill{serviceBills.length === 1 ? "" : "s"} · {INR(serviceSummary?.total_service_amount)}
            </h3>
          </div>
          <a href="/service" className="border border-[#262626] px-4 py-2 hover:border-[#FF5A1F] hover:bg-[#1A1A1A] transition-colors text-sm inline-flex items-center gap-2">
            <Wrench size={14} /> Manage
          </a>
        </div>
        {serviceBills.length === 0 ? (
          <div className="p-8 text-center text-[#A3A3A3]">
            No service bills yet. Add them from the <a href="/service" className="text-[#FF5A1F] hover:underline">Service page</a>.
          </div>
        ) : (
          <table className="w-full" data-testid="vehicle-service-table">
            <thead>
              <tr>
                {["Date", "Vendor", "Items", "Total", "Bill", "Remarks"].map((h) => (
                  <th key={h} className="text-left py-4 px-3 text-xs uppercase tracking-wider font-bold text-[#737373] border-b border-[#262626]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="font-mono text-sm">
              {serviceBills.map((b) => (
                <tr key={b.id} className="border-b border-[#262626] hover:bg-[#1A1A1A] transition-colors">
                  <td className="py-3 px-3 text-[#A3A3A3]">{(b.date || "").slice(0, 10)}</td>
                  <td className="py-3 px-3">{b.vendor || "—"}</td>
                  <td className="py-3 px-3 text-[#A3A3A3]">
                    {b.items?.length ? (
                      <details><summary className="cursor-pointer">{b.items.length} items</summary>
                        <ul className="pl-4 pt-1 text-xs">
                          {b.items.map((it, i) => (<li key={i} className="text-[#A3A3A3]">{it.description} — ₹{it.cost}</li>))}
                        </ul>
                      </details>
                    ) : "—"}
                  </td>
                  <td className="py-3 px-3 text-white font-bold">{INR(b.total_amount)}</td>
                  <td className="py-3 px-3">{b.bill_path ? <ReceiptViewer path={b.bill_path} /> : <ReceiptEmpty />}</td>
                  <td className="py-3 px-3 text-[#A3A3A3] max-w-xs truncate">{b.remarks || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon, testid, accent }) {
  return (
    <div className="ft-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between text-[#737373]">
        <span className="ft-overline">{label}</span>
        <span className="text-[#FF5A1F]">{icon}</span>
      </div>
      <div className={`font-mono text-2xl font-medium mt-4 ${accent ? "text-[#FF5A1F]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function VehicleAskWrapper({ vehicleId }) {
  const { flag } = useEntitlements();
  if (!flag("ai_ask")) return null;
  return <AskRayFuel vehicleId={vehicleId} className="mt-6" />;
}

